<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <div class="col-12 text-center text-sm-left mb-0">
        <span class="text-uppercase page-subtitle d-block">
          {{ $t('huntingWordsGenerator.list.subtitle') }}
        </span>
        <h3 class="page-title d-inline">
          {{ $t('huntingWordsGenerator.list.title') }}
        </h3>
        <router-link
          class="btn btn-success text-white float-right"
          :to="{ name: 'admin-hunting-words-new' }"
        >
          {{ $t('huntingWordsGenerator.list.add') }}
        </router-link>
      </div>
    </d-row>

    <!-- Default Light Table -->
    <d-row>
      <d-col>
        <d-card class="card-small mb-4">
          <div class="card-header border-bottom d-flex justify-content-around">
            <div class="col-6">
              <h6 class="m-0">{{ $t('huntingWordsGenerator.table.title') }}</h6>
            </div>
            <div class="col-6">
              <form class="main-sidebar__search w-100 d-flex">
                <div class="input-group input-group-seamless ml-3">
                  <div class="input-group-prepend">
                    <div class="input-group-text">
                      <i class="fas fa-search"></i>
                    </div>
                  </div>
                  <input
                    class="navbar-search form-control"
                    type="text"
                    :placeholder="
                      $t('huntingWordsGenerator.table.searchPlaceholder')
                    "
                    :aria-label="$t('shared.actions.search')"
                    v-model="pagination.search"
                  />
                </div>
              </form>
            </div>
          </div>
          <div class="card-body p-0 pb-3 text-left">
            <div class="table-overflow">
              <puzzle-list-table
                ref="puzzleList"
                :puzzles="puzzle.list"
                eventName="Delete:HuntingWords"
                :exportable="true"
                export-game-type="wordsearch"
                :sendable="true"
                delegate-send
                @requestSend="openSendModal"
              >
                <div
                  slot="actions"
                  slot-scope="{ puzzleId, puzzleName, hasChildren }"
                >
                  <d-link
                    :to="{
                      name: 'admin-hunting-words-edit',
                      params: { puzzleId },
                    }"
                  >
                    {{ puzzleName }}
                    <template v-if="hasChildren">
                      <i
                        :id="`tooltip-grouped-${puzzleId}`"
                        class="fas fa-folder text-muted ml-1"
                        style="cursor: pointer"
                      />
                      <d-tooltip
                        :target="`tooltip-grouped-${puzzleId}`"
                        placement="top"
                      >
                        {{ $t('huntingWordsGenerator.table.groupedTooltip') }}
                      </d-tooltip>
                    </template>
                  </d-link>
                </div>
              </puzzle-list-table>
            </div>
            <pagination
              :total-items="puzzle.total_items"
              :pagination.sync="pagination"
            />
            <send-to-site-modal
              :children="sendChildren"
              @confirm="handleSendToSite"
            />
          </div>
        </d-card>
      </d-col>
    </d-row>
  </d-container>
</template>

<script>
import { mapActions } from 'vuex';

import Events from '_utils/events';
import Pagination from '_shared/Pagination.vue';
import PuzzleListTable from '_shared/PuzzleListTable.vue';
import PuzzleService from '_services/rest/modules/puzzle';
import SendToSiteModal from '../components/SendToSiteModal.vue';

export default {
  name: 'puzzle-list',
  components: {
    Pagination,
    PuzzleListTable,
    SendToSiteModal,
  },
  data() {
    return {
      sendChildren: [],
      pagination: {
        search: '',
        slug: 'caca-palavras',
        skip: (this.$route.params.page - 1) * 10,
        take: 10,
        page: parseInt(this.$route.params.page, Number),
      },
    };
  },
  computed: {
    puzzle() {
      const { puzzleList } = this.$store.state.puzzle;
      if (puzzleList && puzzleList.list.length) {
        return puzzleList;
      }
      return {
        list: [],
        total_items: 0,
      };
    },
  },
  watch: {},
  methods: {
    ...mapActions([
      'setToggle',
      'getPuzzleList',
      'activeAlert',
      'showLoader',
      'hideLoader',
    ]),
    getList() {
      this.getPuzzleList(this.pagination);
    },
    openSendModal({ ids }) {
      this.showLoader();

      Promise.all(ids.map(id => PuzzleService.getPuzzleById(id)))
        .then(puzzles => {
          const children = this.collectSendChildren(puzzles);

          if (children.length === 0) {
            this.activeAlert({
              message: this.$t('huntingWordsGenerator.sendModal.noChildren'),
              theme: 'warning',
            });
            return;
          }

          this.sendChildren = children;
          this.setToggle({ type: 'send_hunting_words_modal', bool: true });
        })
        .catch(() => {
          this.activeAlert({
            message: this.$t('huntingWordsGenerator.alert.sendError'),
            theme: 'danger',
          });
        })
        .finally(() => {
          this.hideLoader();
        });
    },
    collectSendChildren(puzzles) {
      const showParent = puzzles.length > 1;
      const children = [];

      puzzles.forEach(puzzle => {
        const puzzleChildren = Array.isArray(puzzle?.children)
          ? puzzle.children.filter(child => child && child.id)
          : [];

        if (puzzleChildren.length > 0) {
          puzzleChildren.forEach((child, index) => {
            children.push({
              id: child.id,
              name: this.sendChildLabel(child, index, showParent, puzzle),
            });
          });
          return;
        }

        if (puzzle?.id) {
          children.push({
            id: puzzle.id,
            name: puzzle.name,
          });
        }
      });

      return children;
    },
    sendChildLabel(child, index, showParent, puzzle) {
      const ownName =
        child.name ||
        this.$t('huntingWordsGenerator.form.game', { n: index + 1 });

      if (showParent && puzzle?.name) {
        return `${puzzle.name} — ${ownName}`;
      }

      return ownName;
    },
    handleSendToSite({ ids }) {
      PuzzleService.send(ids)
        .then(() => {
          this.activeAlert({
            message: this.$t('huntingWordsGenerator.alert.sendSuccess'),
            theme: 'success',
          });
          this.leaveSelection();
        })
        .catch(() => {
          this.activeAlert({
            message: this.$t('huntingWordsGenerator.alert.sendError'),
            theme: 'danger',
          });
        });
    },
    leaveSelection() {
      const list = this.$refs.puzzleList;
      if (list && list.isSelectionMode) {
        list.toggleSelectionMode();
      }
    },
  },
  destroyed() {
    Events.$off('update:pagination');
  },
  mounted() {
    Events.$emit('sidebarVisible', false);
    Events.$on('update:pagination', pagination => {
      this.$router.push({ params: { page: pagination.page } });
      this.getList();
    });
    this.getList();
  },
};
</script>
